import { callAzureOpenAIChat } from './azureOpenAIChat';
import { LAYER_AI_SYSTEM_PROMPT } from './layerNameAI.prompt';

// Cap how many candidates we send — large lists make the model "pick
// something" even when nothing fits.
const MAX_CANDIDATES = 60;

export interface LayerCandidate {
  id: string;
  name: string;
}

/**
 * Verdict from the classifier. `layerId` and `isHumanName` are independent:
 * the model can flag both, neither, or just one.
 */
export interface AIClassification {
  layerId: string | null;
  layerName: string | null;
  isHumanName: boolean;
}

// Keyed by query+sorted-ids so a change in available layers invalidates.
const matchCache = new Map<string, AIClassification | null>();

function cacheKey(query: string, candidates: LayerCandidate[]): string {
  const ids = candidates
    .map(c => c.id)
    .sort()
    .join(',');
  return `${query.trim().toLowerCase()}|${ids}`;
}

/**
 * Classify the user's query via Azure OpenAI. Returns `null` when
 * classification is unavailable (no creds, network/parse failure, abort,
 * timeout) so callers can fall back to other heuristics with one branch.
 */
export async function classifyQueryAsLayer(
  query: string,
  candidates: LayerCandidate[],
  signal: AbortSignal,
): Promise<AIClassification | null> {
  const trimmed = query.trim();
  if (!trimmed || candidates.length === 0) return null;

  const sliced = candidates.slice(0, MAX_CANDIDATES);
  const key = cacheKey(trimmed, sliced);
  if (matchCache.has(key)) {
    const cached = matchCache.get(key) ?? null;
    // eslint-disable-next-line no-console
    console.log('[LayerNameAI]', `"${trimmed}"`, '→ cached:', cached);
    return cached;
  }

  const userPayload = JSON.stringify({
    query: trimmed,
    layers: sliced.map(c => ({ id: c.id, name: c.name })),
  });

  const content = await callAzureOpenAIChat(
    [
      { role: 'system', content: LAYER_AI_SYSTEM_PROMPT },
      { role: 'user', content: userPayload },
    ],
    signal,
    { temperature: 0, maxTokens: 60, jsonMode: true },
  );
  if (!content) {
    // eslint-disable-next-line no-console
    console.log(
      '[LayerNameAI]',
      `"${trimmed}"`,
      '→ classify failed (no response from Azure OpenAI)',
    );
    return null;
  }

  let parsed: { layerId?: unknown; isHumanName?: unknown };
  try {
    parsed = JSON.parse(content) as typeof parsed;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log(
      '[LayerNameAI]',
      `"${trimmed}"`,
      '→ failed to parse model JSON:',
      err,
      content,
    );
    return null;
  }

  const rawLayerId =
    typeof parsed.layerId === 'string' && parsed.layerId.length > 0
      ? parsed.layerId
      : null;
  const isHumanName = parsed.isHumanName === true;

  // eslint-disable-next-line no-console
  console.log(
    '[LayerNameAI]',
    `"${trimmed}"`,
    '→ raw model JSON:',
    content.trim(),
  );

  let layerId: string | null = null;
  let layerName: string | null = null;
  if (rawLayerId) {
    const match = sliced.find(c => c.id === rawLayerId);
    if (match) {
      layerId = match.id;
      layerName = match.name;
    } else {
      // eslint-disable-next-line no-console
      console.log(
        '[LayerNameAI]',
        `"${trimmed}"`,
        '→ AI returned layer id',
        JSON.stringify(rawLayerId),
        '— not in candidate list (ignored)',
      );
    }
  }

  const result: AIClassification = { layerId, layerName, isHumanName };
  // eslint-disable-next-line no-console
  console.log('[LayerNameAI]', `"${trimmed}"`, '→ AI thinks:', {
    layerName: layerName ?? '(not a layer name)',
    isHumanName,
  });
  matchCache.set(key, result);
  return result;
}
